/**
 * arbi watch — stream workspace activity in real time
 *
 * Flags:
 *   --timeout <seconds>  Auto-close after N seconds
 *   --count <n>          Stop after N messages
 *   --json               Output NDJSON for piping
 */

import { Command } from 'commander'
import { connectWebSocket, formatWsMessage, type MessageLevel } from '@arbidocs/sdk'
import { runAction, resolveWorkspace } from '../helpers.js'
import { chalk } from '../output.js'

function colorize(level: MessageLevel, text: string): string {
  if (level === 'success') return chalk.green(text)
  if (level === 'error') return chalk.red(text)
  if (level === 'warning') return chalk.yellow(text)
  return text
}

interface WatchOpts {
  workspace?: string
  timeout?: string
  count?: string
  json?: boolean
  type?: string
}

/**
 * Known event types emitted by the workspace WebSocket. Used both for the
 * `--type` help text and for runtime validation so a typo doesn't silently
 * drop every event.
 */
const KNOWN_WATCH_TYPES = [
  'task_update',
  'batch_complete',
  'presence_update',
  'response_complete',
  'notification',
  'error',
] as const

export function registerWatchCommand(program: Command): void {
  program
    .command('watch')
    .description('Watch workspace activity in real time')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-t, --timeout <seconds>', 'Auto-close after N seconds')
    .option('-n, --count <n>', 'Stop after N messages')
    .option(
      '--type <comma-list>',
      `Only emit events of these types (default: all). Known: ${KNOWN_WATCH_TYPES.join(',')}`
    )
    .option('--json', 'Output NDJSON (one JSON object per line)')
    .action((opts: WatchOpts) =>
      runAction(async () => {
        const { config, accessToken, workspaceId } = await resolveWorkspace(opts.workspace, {
          skipNotifications: true,
        })
        const timeoutSec = opts.timeout ? parseInt(opts.timeout, 10) : undefined
        const maxCount = opts.count ? parseInt(opts.count, 10) : undefined
        const jsonMode = opts.json ?? false

        // Resolve the `--type` filter once up front. Validating against the
        // known list catches typos like `--type messages` (instead of
        // `notification`) immediately rather than silently swallowing every
        // event for the lifetime of the watch.
        const typeFilter = opts.type
          ? new Set(
              opts.type
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean)
            )
          : null
        if (typeFilter) {
          const unknown = [...typeFilter].filter(
            (t) => !(KNOWN_WATCH_TYPES as readonly string[]).includes(t)
          )
          if (unknown.length > 0) {
            console.error(
              `Error: unknown event type(s): ${unknown.join(', ')}\n` +
                `Known: ${KNOWN_WATCH_TYPES.join(', ')}`
            )
            process.exit(1)
          }
        }

        if (!jsonMode) {
          const parts = [`Watching workspace ${workspaceId}...`]
          if (timeoutSec) parts.push(`(timeout: ${timeoutSec}s)`)
          if (maxCount) parts.push(`(max: ${maxCount} messages)`)
          parts.push('(Ctrl+C to stop)')
          console.log(parts.join(' '))
        }

        let messageCount = 0
        let timedOut = false
        let onDone!: () => void
        const done = new Promise<void>((r) => {
          onDone = r
        })

        const conn = await connectWebSocket({
          baseUrl: config.baseUrl,
          accessToken,
          onMessage: (msg) => {
            if (typeFilter) {
              const type = (msg as Record<string, unknown>).type as string | undefined
              if (!type || !typeFilter.has(type)) return
            }
            messageCount++

            if (jsonMode) {
              console.log(JSON.stringify(msg))
            } else {
              const { text, level } = formatWsMessage(msg)
              console.log(colorize(level, text))
            }

            if (maxCount && messageCount >= maxCount) {
              if (!jsonMode) console.log(chalk.dim(`\nReached ${maxCount} messages, closing.`))
              conn.close()
            }
          },
          onClose: (code, reason) => {
            if (!jsonMode) {
              console.log(
                chalk.yellow(`\nConnection closed (code ${code}${reason ? ': ' + reason : ''})`)
              )
            }
            onDone()
          },
        })

        // Timeout auto-close
        let timer: ReturnType<typeof setTimeout> | undefined
        if (timeoutSec) {
          timer = setTimeout(() => {
            timedOut = true
            if (!jsonMode) console.log(chalk.dim(`\nTimeout (${timeoutSec}s), closing.`))
            conn.close()
          }, timeoutSec * 1000)
        }

        // Clean SIGINT handler
        const sigintHandler = () => {
          if (timer) clearTimeout(timer)
          conn.close()
        }
        process.on('SIGINT', sigintHandler)

        await done

        // Cleanup
        if (timer) clearTimeout(timer)
        process.removeListener('SIGINT', sigintHandler)

        // If the caller asked for N messages and we never got them, surface
        // that explicitly. Old behavior was exit 0 with no output — agents
        // couldn't tell "got it" from "still waiting." Match GNU `timeout`'s
        // 124 exit code and emit a terminator JSON line for NDJSON consumers.
        if (timedOut && maxCount && messageCount < maxCount) {
          if (jsonMode) {
            console.log(
              JSON.stringify({
                type: 'timeout',
                received: messageCount,
                expected: maxCount,
                timeout_seconds: timeoutSec,
              })
            )
          } else {
            console.error(
              chalk.yellow(`Timed out before reaching ${maxCount} messages (got ${messageCount}).`)
            )
          }
          process.exit(124)
        }
      })()
    )
}
